import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Gift, Info } from 'lucide-react';

const AlertNotification = ({
  message,
  title = 'Missing Information',
  onClose,
  show,
  type = 'error',
  onViewPlayers = null
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      // Trigger animation after render
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!shouldRender) return null;

  // Determine alert styling based on type
  const getAlertConfig = () => {
    switch (type) {
      case 'INVALID':
        return { backgroundColor: '#540008ff', Icon: AlertTriangle, isSpecial: false }; // Red
      case 'VALID':
        return { backgroundColor: '#00701aff', Icon: CheckCircle, isSpecial: false }; // Green
      case 'REWARD':
        return { backgroundColor: '#00701aff', Icon: Gift, isSpecial: false }; // Coca-Cola Red with special layout
      case 'DOUBLE_SAME':
      case 'DOUBLE_DIFF':
      default:
        return { backgroundColor: '#000', Icon: Info, isSpecial: false }; // Black
    }
  };

  const { backgroundColor, Icon, isSpecial } = getAlertConfig();

  return (
    <div
      className="position-fixed top-0 start-0 end-0 mx-auto px-3 py-3"
      style={{
        zIndex: 9999,
        maxWidth: '480px',
        marginTop: '1rem',
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div
        className="alert mb-0 rounded-4 shadow-lg"
        role="alert"
        style={{
          backgroundColor,
          color: '#fff',
          border: 'none',
          padding: isSpecial ? '1.5rem' : '1.25rem 1.5rem'
        }}
      >
        {isSpecial ? (
          // Special REWARD layout with Coke image
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center gap-3 flex-grow-1">
              <div className="flex-shrink-0">
                <img
                  src="/assets/images/app-image/congratulations-alert-icon.png"
                  alt="Coke Bottle"
                  style={{
                    width: '100px',
                    height: '100px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              </div>
              <div className="flex-grow-1">
                <h6 className="mb-1 text-start fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.1rem' }}>
                  <Icon size={20} />
                  {title}
                </h6>
                <p className="mb-0 text-start" style={{ fontSize: '0.9rem', opacity: 0.95 }}>
                  {message}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white ms-3 flex-shrink-0"
              onClick={handleClose}
              aria-label="Close"
              style={{ fontSize: '0.875rem' }}
            ></button>
          </div>
        ) : (
          // Standard layout for other types
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-start gap-3 flex-grow-1">
              <Icon size={24} className="flex-shrink-0 mt-1" />
              <div className="flex-grow-1">
                <h6 className="mb-1 text-start fw-bold" style={{ fontSize: '1rem' }}>
                  {title}
                </h6>
                <p className="mb-0 text-start" style={{ fontSize: '0.87rem', opacity: 0.9 }}>
                  {message}
                </p>
                {/* Everybody Wins button — shown for VALID / REWARD when feature is active */}
                {onViewPlayers && (type === 'VALID' || type === 'REWARD') && (
                  <div>
                    <p className="mt-3 mb-0 text-start fw-bold" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                      You unveiled FOUR iconic players! Want to see them all?
                    </p>
                    <button
                      className="btn btn-light rounded-pill fw-medium mt-3 py-2 w-100"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => { handleClose(); setTimeout(onViewPlayers, 350); }}
                    >
                      Click here to view them
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white ms-3 flex-shrink-0"
              onClick={handleClose}
              aria-label="Close"
              style={{ fontSize: '0.875rem' }}
            ></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertNotification;
